import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from '../lib/axios';
import { 
  handlePostLoginFlow, 
  handleStoreSelection, 
  handleInvitationAcceptance, 
  clearStoreData,
  shouldShowStoreHub,
  FlowResult,
  PostLoginFlowParams
} from '../utils/handlePostLoginFlow';
import { NavigateFunction } from 'react-router-dom';

/**
 * React hook for unified post-login flow management
 */
export function usePostLoginFlow(navigate?: NavigateFunction) {
  const { user, stores, selectStore, refreshStores, currentShop } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingInvitations, setPendingInvitations] = useState<any[]>([]);
  const [isExecuting, setIsExecuting] = useState(false);

  // Main post-login flow handler
  const executePostLoginFlow = async (invitations?: any[], navFunction?: NavigateFunction) => {
    if (!user || isExecuting) return;

    setIsExecuting(true);
    
    // Add timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      console.warn('Post-login flow timeout - forcing completion');
      setIsExecuting(false);
      setError('Post-login flow took too long. Please refresh the page.');
    }, 30000); // 30 second timeout

    const params: PostLoginFlowParams = {
      user,
      stores,
      invitations: invitations || pendingInvitations,
      currentShop,
      selectStore: async (shop: string) => {
        selectStore(shop);
        return Promise.resolve();
      },
      refreshStores,
      navigate: navFunction || navigate
    };

    try {
      setIsLoading(true);
      setError(null);
      const result = await handlePostLoginFlow(params);
      // Removed excessive debug logging to prevent system hanging
      
      // Clear timeout on success
      clearTimeout(timeoutId);

      if (result.action === 'redirect' && result.destination) {
        const nav = navigate;
        if (nav) {
          nav(result.destination);
        }
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to execute post-login flow';
      setError(errorMessage);
      console.error('Post-login flow error:', err);
      
      // Clear timeout on error
      clearTimeout(timeoutId);
      
      // Check if user needs onboarding before falling back to stores
      const needsOnboarding = Number(user?.skip) === 0;
      const hasInvitationToken = localStorage.getItem('invitation_token') || 
                                sessionStorage.getItem('invitation_token') ||
                                window.location.search.includes('invite');
      
      if (needsOnboarding && !hasInvitationToken) {
        console.log('Error fallback: User needs onboarding, redirecting to /on-boarding');
        const nav = navigate;
        if (nav) {
          nav('/on-boarding');
        }
      } else {
        // Fallback: try to redirect to dashboard for members
        if (stores.some((s) => s.user_role === 'member')) {
          const nav = navigate;
          if (nav) {
            nav('/');
          }
        }
      }
      return { action: 'error', destination: null };
    } finally {
      setIsLoading(false);
      setIsExecuting(false);
    }
  };

  // Store selection handler
  const selectAndNavigateToStore = async (store: any, navFunction?: NavigateFunction) => {
    setIsLoading(true);
    setError(null);

    // Add timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      console.warn('Store selection timeout - forcing completion');
      setIsLoading(false);
      setError('Store selection took too long. Please refresh the page.');
    }, 15000); // 15 second timeout

    try {
      // Clear previous store data
      clearStoreData();
      
      // Handle store selection - wrap selectStore to handle async
      const asyncSelectStore = async (shop: string) => {
        selectStore(shop);
        return Promise.resolve();
      };
      
      const nav = navFunction || navigate;
      if (nav) {
        await handleStoreSelection(store, asyncSelectStore, nav, stores);
      }
      
      // Clear timeout on success
      clearTimeout(timeoutId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to select store';
      setError(errorMessage);
      console.error('Store selection error:', err);
      
      // Clear timeout on error
      clearTimeout(timeoutId);
    } finally {
      setIsLoading(false);
    }
  };

  // Invitation acceptance handler
  const acceptInvitation = async (invitation: any, navFunction?: NavigateFunction) => {
    setIsLoading(true);
    setError(null);

    // Add timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      console.warn('Invitation acceptance timeout - forcing completion');
      setIsLoading(false);
      setError('Invitation acceptance took too long. Please refresh the page.');
    }, 20000); // 20 second timeout

    try {
      const nav = navFunction || navigate;
      if (nav) {
        await handleInvitationAcceptance(invitation, refreshStores, nav);
      }
      
      // After accepting invitation, re-run the post-login flow only if not already in post-login flow
      // This prevents infinite loop when executePostLoginFlow is called with undefined
      if (!isLoading) {
        setTimeout(() => {
          executePostLoginFlow(undefined, navFunction);
        }, 500);
      }
      
      // Clear timeout on success
      clearTimeout(timeoutId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to accept invitation';
      setError(errorMessage);
      console.error('Invitation acceptance error:', err);
      
      // Clear timeout on error
      clearTimeout(timeoutId);
    } finally {
      setIsLoading(false);
    }
  };

  // Load pending invitations
  const loadPendingInvitations = async () => {
    if (!user || !localStorage.getItem('token')) return;

    try {
      const response = await axios.get('/invitations/user/pending');
      setPendingInvitations(response.data.invitations || []);
    } catch (err) {
      console.error('Failed to load pending invitations:', err);
    }
  };

  // Listen for store change events to clear data
  useEffect(() => {
    const handleStoreChange = () => {
      clearStoreData();
    };

    window.addEventListener('storeChanged', handleStoreChange);
    return () => window.removeEventListener('storeChanged', handleStoreChange);
  }, []);

  return {
    // State
    isLoading,
    error,
    pendingInvitations,
    
    // Actions
    executePostLoginFlow,
    selectAndNavigateToStore,
    acceptInvitation,
    loadPendingInvitations,
    
    // Utilities
    shouldShowStoreHub: shouldShowStoreHub(stores, pendingInvitations),
    clearError: () => setError(null)
  };
}
